// Tiny wrapper around `startPurchase` (from useHook usePurchase) that:
//   - Calls startPurchase with the given params.
//   - If the result is `{ alreadyOwned: true }` (set by playBilling.js when
//     Google Play returns BILLING_RESPONSE_RESULT_ITEM_ALREADY_OWNED), shows
//     a clear toast with a "Réessayer" action button. The button re-invokes
//     this same wrapper — internally, playBilling.js will first consume the
//     stuck purchase server-side, then re-open the buy popup.
//   - Tracks `purchase_attempted` (before the buy popup) and `purchase_completed`
//     (with `amount` + `plan` properties) via Mixpanel.
//   - Returns the final result for the caller to handle success/normal-error.

import { track } from './analytics';

const RETRY_LABEL = {
  fr: 'Réessayer',
  en: 'Retry',
  es: 'Reintentar',
  de: 'Erneut versuchen',
  pt: 'Tentar novamente',
};

// itemType -> plan label used in analytics. Stable & human-readable.
const planLabel = (itemType, itemId) => {
  if (itemType === 'subscription') return 'premium_monthly';
  if (itemType === 'photos') return 'photo_pack';
  if (itemType === 'level') return 'level_pack';
  if (itemType === 'character') return `character_${itemId}`;
  return itemType || 'unknown';
};

// Approximate prices (EUR) — used only as an analytics fallback when the
// backend doesn't return a confirmed `amount`. Stripe/Play always charge the
// real configured price; this is for funnel reporting only.
const FALLBACK_PRICE_EUR = {
  subscription: 9.99,
  photos: 1.49,
  level: 1.49,
  character: 1.49,
};

export async function runPurchaseWithRetry({ startPurchase, params, toast, language = 'fr' }) {
  const plan = planLabel(params?.itemType, params?.itemId);
  track('purchase_attempted', { item_type: params?.itemType, item_id: params?.itemId, plan });

  const res = await startPurchase(params);

  if (res?.success) {
    const amount = res?.amount ?? res?.item?.amount ?? FALLBACK_PRICE_EUR[params?.itemType] ?? null;
    track('purchase_completed', {
      amount,
      currency: 'EUR',
      plan,
      item_type: params?.itemType,
      item_id: params?.itemId,
      provider: res?.provider,
    });
  } else if (res?.alreadyOwned) {
    toast.error(res.message, {
      duration: 10000,
      action: {
        label: RETRY_LABEL[language] || RETRY_LABEL.en,
        onClick: () => runPurchaseWithRetry({ startPurchase, params, toast, language }),
      },
    });
  }
  return res;
}
